/**
 * Owner: Masa Bayu
 * File: demo-reading-continuity.ts
 * Date:Time: 2026-09-18 13:57
 * Description: READER-PROD R3 — persist Reader page progress into Library projection.
 */

import { applyReadingProgress, recordLibraryContinueReading } from '@qx/qxread-core';
import {
  encodeQxpPageRef,
  parseQxpPageRef,
  percentFromPageIndex,
} from '../reader/shared/reading-continuity';
import {
  applyDemoLibraryItemUpdate,
  getDemoLibraryProjection,
  peekDemoEntitlements,
} from './demo-library-store';
import type { QxreadLibraryItem } from '@qx/qxread-core';

function findItem(publicationId: string): QxreadLibraryItem | null {
  const projection = getDemoLibraryProjection();
  const pool = [
    ...projection.purchased,
    ...projection.continueReading,
    ...projection.completed,
    ...projection.blocked,
    ...projection.archived,
  ];
  return pool.find((item) => item.publicationId === publicationId) ?? null;
}

/**
 * Ensure owned title has a QXP page locus without wiping an existing qxp_page_ resume.
 */
export function ensureDemoReadingStarted(publicationId: string): QxreadLibraryItem {
  const item = findItem(publicationId);
  if (item === null) {
    throw new Error('Demo reading start — library item not found');
  }
  if (parseQxpPageRef(item.progress.lastPositionRef) !== null) {
    return item;
  }
  const updated = recordLibraryContinueReading(item, {
    percentComplete: Math.max(item.progress.percentComplete, 1),
    lastPositionRef: encodeQxpPageRef(1),
  });
  applyDemoLibraryItemUpdate(updated);
  return updated;
}

/**
 * Persist Flip/nav page into Library · lastPositionRef = qxp_page_N.
 */
export function persistDemoReadingProgress(
  publicationId: string,
  pageIndex: number,
  pageCount: number,
): QxreadLibraryItem | null {
  const entitlement = peekDemoEntitlements().find((entry) => entry.publicationId === publicationId);
  if (entitlement === undefined) {
    return null;
  }
  const item = findItem(publicationId);
  if (item === null) {
    return null;
  }
  if (item.state === 'blocked' || item.state === 'removed' || item.state === 'archived') {
    return item;
  }

  const clampedPage = Math.max(1, Math.min(Math.max(1, pageCount), Math.floor(pageIndex)));
  const percent = percentFromPageIndex(clampedPage, pageCount);
  const positionRef = encodeQxpPageRef(clampedPage);

  const updated =
    percent >= 100
      ? applyReadingProgress(item, {
          state: 'completed',
          percentComplete: 100,
          lastPositionRef: positionRef,
        })
      : recordLibraryContinueReading(item, {
          percentComplete: percent,
          lastPositionRef: positionRef,
        });

  applyDemoLibraryItemUpdate(updated);
  return updated;
}
